import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { createPromptPreset, deletePromptPreset, listPromptPresets, updatePromptPreset } from 'tripPlanner/api'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { PromptModalResult } from '../../PromptModal'
import type { PromptOption } from '../../PromptSelector'

type StoredPrompt = {
  name: string
  promptId: string
  notes?: string
}

const DEFAULT_PROMPT_KEY = 'current'

interface UsePromptManagementArgs {
  canManagePrompts: boolean
}

interface UsePromptManagementReturn {
  customPrompts: Array<StoredPrompt>
  setCustomPrompts: React.Dispatch<React.SetStateAction<Array<StoredPrompt>>>
  selectedPromptKey: string
  setSelectedPromptKey: React.Dispatch<React.SetStateAction<string>>
  isPromptListLoading: boolean
  setIsPromptListLoading: React.Dispatch<React.SetStateAction<boolean>>
  promptOptions: Array<PromptOption>
  selectedPrompt: PromptOption
  loadPrompts: () => Promise<void>
  handlePromptEdit: (result: PromptModalResult) => Promise<boolean>
  handlePromptAdd: (result: PromptModalResult) => Promise<boolean>
  handlePromptDelete: (promptId: string) => Promise<boolean>
  handlePromptSelect: (key: string) => void
  DEFAULT_PROMPT_OPTION: PromptOption
}

export function usePromptManagement({ canManagePrompts }: UsePromptManagementArgs): UsePromptManagementReturn {
  const intl = useIntl()
  const [customPrompts, setCustomPrompts] = useState<Array<StoredPrompt>>([])
  const [selectedPromptKey, setSelectedPromptKey] = useState<string>(DEFAULT_PROMPT_KEY)
  const [isPromptListLoading, setIsPromptListLoading] = useState(false)

  const DEFAULT_PROMPT_OPTION: PromptOption = useMemo(() => ({
    key: DEFAULT_PROMPT_KEY,
    name: intl.formatMessage({
      id: 'sme9U_dcbUHU7cLY4CrP',
      defaultMessage: 'Master',
      description: 'Default prompt preset name in the trip planner prompt selector',
    }),
    isDefault: true,
  }), [intl])

  const promptOptions = useMemo<Array<PromptOption>>(
    () => [
      DEFAULT_PROMPT_OPTION,
      ...customPrompts.map(p => ({
        key: p.promptId,
        name: p.name,
        promptId: p.promptId,
        notes: p.notes,
      })),
    ],
    [customPrompts, DEFAULT_PROMPT_OPTION],
  )

  const selectedPrompt = useMemo(
    () => promptOptions.find(option => option.key === selectedPromptKey) ?? DEFAULT_PROMPT_OPTION,
    [promptOptions, selectedPromptKey, DEFAULT_PROMPT_OPTION],
  )

  const getPromptErrorMessage = useCallback((error: unknown, fallback: string) => {
    const errorObj = error as { response?: { data?: { message?: string } }; message?: string } | undefined
    const message = errorObj?.response?.data?.message ?? errorObj?.message
    if (typeof message === 'string' && message.toLowerCase().includes('prompt id already exists')) {
      return intl.formatMessage({
        id: 'LOhdQe0HB9m9gCcEGNOD',
        defaultMessage: 'Prompt ID already exists',
        description: 'Error message when trying to create a prompt preset with a duplicate ID',
      })
    }
    return fallback
  }, [intl])

  const loadPrompts = useCallback(async() => {
    if (!canManagePrompts) {
      setCustomPrompts([])
      return
    }
    setIsPromptListLoading(true)
    try {
      const presets = await listPromptPresets()
      setCustomPrompts(
        presets.map(preset => ({
          name: preset.name,
          promptId: preset.promptId,
          notes: preset.notes ?? undefined,
        })),
      )
    } catch {
      setCustomPrompts([])
    } finally {
      setIsPromptListLoading(false)
    }
  }, [canManagePrompts])

  const handlePromptEdit = useCallback(async(result: PromptModalResult): Promise<boolean> => {
    if (!canManagePrompts) {
      return false
    }
    const { name, promptId, notes } = result
    try {
      await updatePromptPreset(promptId, { name, promptId, notes })
      setCustomPrompts(prev =>
        prev.map(p =>
          p.promptId === promptId ? { ...p, name, notes } : p,
        ),
      )
      showSnackbar(intl.formatMessage({
        id: '6dZR2c0n9Hen7mvZMysn',
        defaultMessage: 'Prompt preset updated',
        description: 'Success snackbar message after updating a prompt preset',
      }), 'success')
      return true
    } catch(error) {
      const message = getPromptErrorMessage(error, intl.formatMessage({
        id: 'wMnP6SE4MmP1ynym-5Q1',
        defaultMessage: 'Failed to update prompt preset',
        description: 'Error snackbar message when updating a prompt preset fails',
      }))
      showSnackbar(message, 'critical')
      return false
    }
  }, [canManagePrompts, intl, getPromptErrorMessage])

  const handlePromptDelete = useCallback(async(promptId: string): Promise<boolean> => {
    if (!canManagePrompts) {
      return false
    }
    try {
      await deletePromptPreset(promptId)
      setCustomPrompts(prev => prev.filter(p => p.promptId !== promptId))
      if (selectedPromptKey === promptId) {
        setSelectedPromptKey(DEFAULT_PROMPT_KEY)
      }
      showSnackbar(intl.formatMessage({
        id: 'kj83Dr0zsV-HPweoNOAt',
        defaultMessage: 'Prompt preset deleted',
        description: 'Success snackbar message after deleting a prompt preset',
      }), 'success')
      return true
    } catch(error) {
      const message = getPromptErrorMessage(error, intl.formatMessage({
        id: 'AQwR4M7KZgoPyqJ8LoS0',
        defaultMessage: 'Failed to delete prompt preset',
        description: 'Error snackbar message when deleting a prompt preset fails',
      }))
      showSnackbar(message, 'critical')
      return false
    }
  }, [canManagePrompts, selectedPromptKey, intl, getPromptErrorMessage])

  const handlePromptAdd = useCallback(async(result: PromptModalResult): Promise<boolean> => {
    if (!canManagePrompts) {
      return false
    }
    const { name, promptId, notes } = result
    try {
      await createPromptPreset({ name, promptId, notes })
      setCustomPrompts(prev => [...prev, { name, promptId, notes }])
      showSnackbar(intl.formatMessage({
        id: 'r7WPtOOOuy9uPnV5AIfV',
        defaultMessage: 'Prompt preset created',
        description: 'Success snackbar message after creating a prompt preset',
      }), 'success')
      return true
    } catch(error) {
      const message = getPromptErrorMessage(error, intl.formatMessage({
        id: 'aY8sAu5woz_gkzoQUYo8',
        defaultMessage: 'Failed to create prompt preset',
        description: 'Error snackbar message when creating a prompt preset fails',
      }))
      showSnackbar(message, 'critical')
      return false
    }
  }, [canManagePrompts, intl, getPromptErrorMessage])

  const handlePromptSelect = useCallback((key: string) => {
    setSelectedPromptKey(key)
  }, [])

  return {
    customPrompts,
    setCustomPrompts,
    selectedPromptKey,
    setSelectedPromptKey,
    isPromptListLoading,
    setIsPromptListLoading,
    promptOptions,
    selectedPrompt,
    loadPrompts,
    handlePromptEdit,
    handlePromptAdd,
    handlePromptDelete,
    handlePromptSelect,
    DEFAULT_PROMPT_OPTION,
  }
}
